import type {Image} from '../widgets/types'

export enum ActionType {
  ADD_DESTINATION = 'add_destination',
  OPEN_MAP = 'open_map',
  SAFE_FLOW = 'safe_flow_challenge',
  ORDER = 'order',
  RESET_DUE = 'reset_due',
  SELECT_DUE = 'select_due',
  MODAL = 'modal',
  COUNTER = 'counter',
  CHANGE_STATE = 'change_client_state',
}

export type DateTimeOption = {
  title: string
  description_template: string
  options: {
    from: string
    to: string
    step_minutes: number
    type: 'time_picker'
  }
}

export type BaseActionConfig = {
  type: string
}

export type AddDestinationActionConfig = BaseActionConfig & {
  type: ActionType.ADD_DESTINATION
}

export type OpenMapActionConfig = BaseActionConfig & {
  type: ActionType.OPEN_MAP
}

export type SafeFlowActionConfig = BaseActionConfig & {
  type: ActionType.SAFE_FLOW
  url: string
}

export type OrderActionConfig = BaseActionConfig & {
  type: ActionType.ORDER
  flow: 'route_points'
  widget_id?: string
}

export type ResetDueActionConfig = BaseActionConfig & {
  type: ActionType.RESET_DUE
}

export type SelectDueActionConfig = BaseActionConfig & {
  type: ActionType.SELECT_DUE
  title: string
  slots: Array<DateTimeOption>
  description: {icon: Image}
  metrica_label: string
}

export type ToggleModalActionConfig = BaseActionConfig & {
  type: ActionType.MODAL
  modal_id: string
}

export type Option = {
  title: string
  value: number
  default: boolean
}

export type CounterActionConfig = BaseActionConfig & {
  type: ActionType.COUNTER
  options: Option[]
  form_state_key: string
  metrica_label?: string
}

export type StateChangeActionConfig = BaseActionConfig & {
  type: ActionType.CHANGE_STATE
  state_diff: Record<string, unknown>
}

export type ActionConfig =
  | AddDestinationActionConfig
  | OpenMapActionConfig
  | SafeFlowActionConfig
  | OrderActionConfig
  | ResetDueActionConfig
  | SelectDueActionConfig
  | ToggleModalActionConfig
  | CounterActionConfig
  | StateChangeActionConfig

// ---
// Обогащение action-ов пропами для подписки на их события
// ---

/**
 * Пропы для управления состоянием и подписки на события action
 */
export type ActionComponentProps = {
  /**
   * Флаг активности action
   */
  isActive: boolean
  /**
   * Событие изменения состояния (если action подразумевает такой use-case)
   *
   * Значение, передаваемое в качестве аргумента коллбека, может быть только `string`.
   * Так спроектировано для того, чтобы исключить вариативность типа передаваемого значения и соблюсти явный контракт между widgets и actions
   * @param newValue Новое значение
   * @returns void
   */
  onChange?: (newValue: string) => void
  /**
   * Коллбек с изменением состояния активности action
   * @param isActive Новое состояние
   * @returns void
   */
  setActive?: (isActive: boolean) => void
}

// Объединение конфигурации action и пропов для react-компонентов
export type ActionProps = Extract<ActionConfig, unknown> & ActionComponentProps
/**
 * Утилита для получения пропов экшена по его типу
 */
export type ActionPropsByType<T extends ActionType> = Extract<ActionProps, {type: T}>
